import React, {
	useState,
	useEffect,
	useCallback,
	useRef,
	useMemo,
	forwardRef,
	useImperativeHandle,
} from 'react';
import {
	Box,
	Button,
	Card,
	CardContent,
	IconButton,
	TextField,
	Typography,
	InputAdornment,
	CircularProgress,
	Stack,
	useMediaQuery,
	useTheme,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Alert,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { makeApiRequest } from '../../Shared/Api';
import { useSnackbar } from 'notistack';
import useConfirm from '../useConfirm';
import {
	createDraftLineItem,
	draftLineItemId,
	formatLineItemCurrency,
	isDraftLineItem,
	isLineItemComplete,
	lineItemTotal,
	quotedLineItemsTotal,
	rowSnapshot,
	toNumber,
} from '../../Shared/lineItems';

const LineItemCard = ({
	row,
	isSaving,
	onUpdate,
	onDelete,
	onSave,
	inputRefs,
}) => {
	const lineTotal = lineItemTotal(row);

	return (
		<Card
			variant="outlined"
			sx={{
				borderRadius: 2,
				bgcolor: isDraftLineItem(row) ? 'action.hover' : 'background.paper',
			}}
		>
			<CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
				<Stack spacing={1.5}>
					<Stack direction="row" alignItems="flex-start" spacing={1}>
						<TextField
							inputRef={(el) => {
								inputRefs.current[`${row._key}-description`] = el;
							}}
							value={row.description || ''}
							onChange={(e) =>
								onUpdate(row._key, { description: e.target.value })
							}
							onBlur={() => onSave(row._key, { quiet: true })}
							placeholder="What are you charging for? (optional)"
							label="Description (optional)"
							fullWidth
							size="small"
							multiline
							minRows={2}
							disabled={isSaving}
						/>
						<IconButton
							size="small"
							color="error"
							onClick={() => onDelete(row)}
							disabled={isSaving}
							sx={{ mt: 0.5 }}
							aria-label="Delete line item"
						>
							<DeleteIcon fontSize="small" />
						</IconButton>
					</Stack>

					<Stack direction="row" spacing={1.5}>
						<TextField
							inputRef={(el) => {
								inputRefs.current[`${row._key}-quantity`] = el;
							}}
							value={row.quantity ?? ''}
							onChange={(e) => onUpdate(row._key, { quantity: e.target.value })}
							onBlur={() => onSave(row._key, { quiet: true })}
							label="Qty"
							type="number"
							inputMode="numeric"
							size="small"
							fullWidth
							slotProps={{ htmlInput: { min: 1, step: 1 } }}
							disabled={isSaving}
						/>
						<TextField
							inputRef={(el) => {
								inputRefs.current[`${row._key}-unit_price`] = el;
							}}
							value={row.unit_price ?? ''}
							onChange={(e) =>
								onUpdate(row._key, { unit_price: e.target.value })
							}
							onBlur={() => onSave(row._key, { quiet: true })}
							label="Rate"
							type="number"
							inputMode="decimal"
							size="small"
							fullWidth
							slotProps={{
								input: {
									startAdornment: (
										<InputAdornment position="start">$</InputAdornment>
									),
								},
								htmlInput: { min: 0, step: '0.01' },
							}}
							disabled={isSaving}
						/>
					</Stack>

					<Stack
						direction="row"
						justifyContent="space-between"
						alignItems="center"
					>
						<Typography variant="caption" color="text.secondary">
							Line total
						</Typography>
						{isSaving ? (
							<CircularProgress size={16} />
						) : (
							<Typography variant="subtitle2" fontWeight={700}>
								{formatLineItemCurrency(lineTotal)}
							</Typography>
						)}
					</Stack>
				</Stack>
			</CardContent>
		</Card>
	);
};

const LineItemsEditor = forwardRef(
	(
		{
			bidId,
			items: controlledItems,
			initialItems,
			onItemsChange,
			compact = false,
			showHint = true,
			quoteIncludesStripeFee = false,
		},
		ref,
	) => {
		const isDraftMode = !bidId;
		const theme = useTheme();
		const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
		const [rows, setRows] = useState(() => {
			if (isDraftMode && Array.isArray(initialItems) && initialItems.length) {
				return initialItems.map((item) => ({
					...item,
					_key: item._key || item.id || draftLineItemId(),
				}));
			}
			return [];
		});
		const [isLoading, setIsLoading] = useState(!isDraftMode);
		const [fetchError, setFetchError] = useState(null);
		const [savingIds, setSavingIds] = useState(() => new Set());
		const lastSavedRef = useRef({});
		const rowsRef = useRef([]);
		const inputRefs = useRef({});
		const { enqueueSnackbar } = useSnackbar();
		const { confirm, confirmDialog } = useConfirm();

		const notifyItemsChange = useCallback(
			(nextRows) => {
				if (typeof onItemsChange !== 'function') {
					return;
				}
				if (isDraftMode) {
					onItemsChange(nextRows.filter((row) => isLineItemComplete(row)));
					return;
				}
				onItemsChange(nextRows.filter((row) => !isDraftLineItem(row)));
			},
			[isDraftMode, onItemsChange],
		);

		const setRowState = useCallback(
			(updater) => {
				setRows((prev) => {
					const next = typeof updater === 'function' ? updater(prev) : updater;
					rowsRef.current = next;
					if (isDraftMode) {
						notifyItemsChange(next);
					}
					return next;
				});
			},
			[isDraftMode, notifyItemsChange],
		);

		useEffect(() => {
			if (!isDraftMode) return;
			if (Array.isArray(controlledItems)) {
				const normalized = controlledItems.map((item) => ({
					...item,
					_key: item._key || item.id,
				}));
				setRows(normalized);
				rowsRef.current = normalized;
			}
		}, [isDraftMode, controlledItems]);

		const fetchLineItems = useCallback(async () => {
			if (!bidId) return;
			try {
				setIsLoading(true);
				setFetchError(null);
				const response = await makeApiRequest(
					'GET',
					`/bids/${bidId}/line-items/`,
				);
				const items = (response || []).map((item) => ({
					...item,
					_key: item.id,
				}));
				const snapshots = {};
				items.forEach((item) => {
					snapshots[item.id] = rowSnapshot(item);
				});
				lastSavedRef.current = snapshots;
				setRows(items);
				rowsRef.current = items;
				notifyItemsChange(items);
			} catch (error) {
				const message = error.message || 'Failed to load line items';
				setFetchError(message);
				enqueueSnackbar(message, { variant: 'error' });
			} finally {
				setIsLoading(false);
			}
		}, [bidId, notifyItemsChange, enqueueSnackbar]);

		useEffect(() => {
			if (isDraftMode) {
				setIsLoading(false);
				return;
			}
			fetchLineItems();
		}, [fetchLineItems, isDraftMode]);

		useEffect(() => {
			rowsRef.current = rows;
		}, [rows]);

		const setSaving = (rowKey, saving) => {
			setSavingIds((prev) => {
				const next = new Set(prev);
				if (saving) {
					next.add(rowKey);
				} else {
					next.delete(rowKey);
				}
				return next;
			});
		};

		const updateRow = (rowKey, patch) => {
			setRowState((prev) =>
				prev.map((row) => (row._key === rowKey ? { ...row, ...patch } : row)),
			);
		};

		const saveRow = async (rowKey, { quiet = false } = {}) => {
			const row = rowsRef.current.find((item) => item._key === rowKey);
			if (!row || savingIds.has(rowKey)) {
				return false;
			}

			if (!isLineItemComplete(row)) {
				if (isDraftLineItem(row)) {
					return false;
				}
				if (!quiet) {
					enqueueSnackbar('Quantity and unit price are required.', {
						variant: 'warning',
					});
				}
				return false;
			}

			if (isDraftMode) {
				return true;
			}

			if (
				!isDraftLineItem(row) &&
				rowSnapshot(row) === lastSavedRef.current[row.id]
			) {
				return true;
			}

			const payload = {
				description: (row.description || '').trim(),
				quantity: Math.round(toNumber(row.quantity, 1)),
				unit_price: toNumber(row.unit_price, 0).toFixed(2),
			};

			const isNew = isDraftLineItem(row);
			const method = isNew ? 'POST' : 'PUT';
			const url = isNew
				? `/bids/${bidId}/line-items/`
				: `/bids/${bidId}/line-items/${row.id}/`;

			setSaving(rowKey, true);
			try {
				const saved = await makeApiRequest(method, url, payload);
				lastSavedRef.current[saved.id] = rowSnapshot(saved);
				setRowState((prev) => {
					const updated = prev.map((item) =>
						item._key === rowKey ? { ...saved, _key: saved.id } : item,
					);
					if (!isDraftMode) {
						notifyItemsChange(updated.filter((row) => !isDraftLineItem(row)));
					}
					return updated;
				});
				if (!quiet) {
					enqueueSnackbar(isNew ? 'Line item added' : 'Line item saved', {
						variant: 'success',
					});
				}
				return true;
			} catch (error) {
				enqueueSnackbar(error.message || 'Failed to save line item', {
					variant: 'error',
				});
				return false;
			} finally {
				setSaving(rowKey, false);
			}
		};

		useImperativeHandle(ref, () => ({
			flushPendingSaves: async () => {
				if (isDraftMode) {
					return rowsRef.current.filter((row) => isLineItemComplete(row));
				}
				const pending = rowsRef.current.filter(
					(row) =>
						isDraftLineItem(row) ||
						rowSnapshot(row) !== lastSavedRef.current[row.id],
				);
				for (const row of pending) {
					if (!isLineItemComplete(row)) {
						continue;
					}
					// eslint-disable-next-line no-await-in-loop
					await saveRow(row._key, { quiet: true });
				}
			},
		}));

		const handleDeleteRow = async (row) => {
			if (isDraftLineItem(row)) {
				setRowState((prev) => {
					const updated = prev.filter((item) => item._key !== row._key);
					if (isDraftMode) {
						notifyItemsChange(
							updated.filter((item) => isLineItemComplete(item)),
						);
					}
					return updated;
				});
				return;
			}

			const confirmed = await confirm(
				'Are you sure you want to delete this line item?',
				{
					title: 'Delete Line Item',
					confirmText: 'Delete',
				},
			);
			if (!confirmed) return;

			try {
				await makeApiRequest('DELETE', `/bids/${bidId}/line-items/${row.id}/`);
				delete lastSavedRef.current[row.id];
				setRowState((prev) => {
					const updated = prev.filter((item) => item._key !== row._key);
					notifyItemsChange(updated.filter((item) => !isDraftLineItem(item)));
					return updated;
				});
				enqueueSnackbar('Line item deleted', { variant: 'success' });
			} catch (error) {
				enqueueSnackbar(error.message || 'Failed to delete line item', {
					variant: 'error',
				});
			}
		};

		const handleAddRow = () => {
			const draft = createDraftLineItem();
			setRowState((prev) => [...prev, draft]);
			setTimeout(() => {
				inputRefs.current[`${draft._key}-description`]?.focus?.();
			}, 0);
		};

		const normalizedRows = useMemo(
			() =>
				rows.map((row) => ({
					...row,
					_key: row._key || row.id,
				})),
			[rows],
		);

		const totalAmount = useMemo(
			() => quotedLineItemsTotal(normalizedRows, quoteIncludesStripeFee),
			[normalizedRows, quoteIncludesStripeFee],
		);

		if (isLoading) {
			return (
				<Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
					<CircularProgress size={28} />
				</Box>
			);
		}

		if (fetchError) {
			return (
				<Alert
					severity="error"
					action={
						<Button color="inherit" size="small" onClick={fetchLineItems}>
							Retry
						</Button>
					}
				>
					{fetchError}
				</Alert>
			);
		}

		const footer = (
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					flexWrap: 'wrap',
					gap: 1.5,
					mt: compact ? 1.5 : 2,
				}}
			>
				<Button
					variant={compact ? 'outlined' : 'contained'}
					color="primary"
					startIcon={<AddIcon />}
					onClick={handleAddRow}
					size="small"
				>
					Add Line Item
				</Button>
				<Box sx={{ textAlign: 'right' }}>
					<Typography
						variant={compact ? 'body2' : 'subtitle1'}
						fontWeight={700}
					>
						Total: {formatLineItemCurrency(totalAmount)}
					</Typography>
					{quoteIncludesStripeFee && totalAmount > 0 ? (
						<Typography variant="caption" color="text.secondary">
							Includes card fees in the highest-total line on the customer
							proposal
						</Typography>
					) : null}
				</Box>
			</Box>
		);

		return (
			<Box sx={{ width: '100%' }}>
				{isDesktop ? (
					<TableContainer
						sx={{
							border: '1px solid',
							borderColor: 'divider',
							borderRadius: 2,
							bgcolor: 'background.paper',
							maxHeight: compact ? 360 : undefined,
						}}
					>
						<Table size="small" stickyHeader={compact}>
							<TableHead>
								<TableRow>
									<TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
									<TableCell align="right" sx={{ fontWeight: 700, width: 88 }}>
										Qty
									</TableCell>
									<TableCell align="right" sx={{ fontWeight: 700, width: 120 }}>
										Rate
									</TableCell>
									<TableCell align="right" sx={{ fontWeight: 700, width: 100 }}>
										Total
									</TableCell>
									<TableCell align="center" sx={{ width: 52 }} />
								</TableRow>
							</TableHead>
							<TableBody>
								{normalizedRows.length === 0 ? (
									<TableRow>
										<TableCell
											colSpan={5}
											sx={{ py: 3, color: 'text.secondary' }}
										>
											No line items yet. Add your first item below.
										</TableCell>
									</TableRow>
								) : (
									normalizedRows.map((row) => {
										const lineTotal = lineItemTotal(row);
										const isSaving = savingIds.has(row._key);

										return (
											<TableRow
												key={row._key}
												sx={{
													bgcolor: isDraftLineItem(row)
														? 'action.hover'
														: 'transparent',
												}}
											>
												<TableCell sx={{ verticalAlign: 'top' }}>
													<TextField
														inputRef={(el) => {
															inputRefs.current[`${row._key}-description`] = el;
														}}
														value={row.description || ''}
														onChange={(e) =>
															updateRow(row._key, {
																description: e.target.value,
															})
														}
														onBlur={() => saveRow(row._key, { quiet: true })}
														placeholder="e.g. Cabinet install (optional)"
														fullWidth
														variant="standard"
														multiline
														maxRows={compact ? 2 : 4}
														disabled={isSaving}
													/>
												</TableCell>
												<TableCell align="right" sx={{ verticalAlign: 'top' }}>
													<TextField
														value={row.quantity ?? ''}
														onChange={(e) =>
															updateRow(row._key, {
																quantity: e.target.value,
															})
														}
														onBlur={() => saveRow(row._key, { quiet: true })}
														type="number"
														variant="standard"
														sx={{ maxWidth: 72 }}
														disabled={isSaving}
													/>
												</TableCell>
												<TableCell align="right" sx={{ verticalAlign: 'top' }}>
													<TextField
														value={row.unit_price ?? ''}
														onChange={(e) =>
															updateRow(row._key, {
																unit_price: e.target.value,
															})
														}
														onBlur={() => saveRow(row._key, { quiet: true })}
														type="number"
														variant="standard"
														sx={{ maxWidth: 104 }}
														slotProps={{
															input: {
																startAdornment: (
																	<InputAdornment position="start">
																		$
																	</InputAdornment>
																),
															},
														}}
														disabled={isSaving}
													/>
												</TableCell>
												<TableCell
													align="right"
													sx={{ fontWeight: 600, verticalAlign: 'top' }}
												>
													{isSaving ? (
														<CircularProgress size={16} />
													) : (
														formatLineItemCurrency(lineTotal)
													)}
												</TableCell>
												<TableCell align="center" sx={{ verticalAlign: 'top' }}>
													<IconButton
														size="small"
														color="error"
														onClick={() => handleDeleteRow(row)}
														disabled={isSaving}
														aria-label="Delete line item"
													>
														<DeleteIcon fontSize="small" />
													</IconButton>
												</TableCell>
											</TableRow>
										);
									})
								)}
							</TableBody>
						</Table>
					</TableContainer>
				) : (
					<Stack spacing={1.5}>
						{normalizedRows.length === 0 ? (
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{ py: 2, textAlign: 'center' }}
							>
								No line items yet. Tap below to add your first charge.
							</Typography>
						) : (
							normalizedRows.map((row) => (
								<LineItemCard
									key={row._key}
									row={row}
									isSaving={savingIds.has(row._key)}
									onUpdate={updateRow}
									onDelete={handleDeleteRow}
									onSave={saveRow}
									inputRefs={inputRefs}
								/>
							))
						)}
					</Stack>
				)}

				{footer}

				{confirmDialog}
			</Box>
		);
	},
);

export default LineItemsEditor;
